import { useTranslation } from 'react-i18next';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import styles from './PendingTranscriptionPill.module.css';

/**
 * Top-center pill on PrayPage chrome (B.2). Auto-shows when the audio
 * queue has any pending entries, auto-hides when the queue clears.
 * Read-only — taps don't open anything.
 *
 * Per E.7 ruling: calm low-contrast surface, NOT brand gold. This is
 * utility chrome, not a primary affordance. Pluralized via i18n.
 *
 * Anchor: top-center, above the textarea, beside the settings gear
 * (top-right) and the copy area (top-left when text present).
 * No top-center surface exists today, so no z-index collision.
 */
export default function PendingTranscriptionPill() {
  const { t } = useTranslation();
  const pendingCount = useAudioQueueStore((s) => s.pendingCount);

  // Audio-only rows intentionally park without implying progress while the
  // independently gated cloud STT path is off.
  if (!enhancedVoiceAllowed() || pendingCount === 0) return null;

  return (
    <div
      className={styles.pill}
      role="status"
      aria-live="polite"
      aria-label={t('voice.queuedPillAria')}
    >
      <span className={styles.spinner} aria-hidden="true" />
      <span className={styles.text}>
        {t('voice.queuedPill', { count: pendingCount })}
      </span>
    </div>
  );
}
